<template>
  <div class="total-barchart i-mb-16">
    <div class="total-barchart__header d-flex justify-content-between">
      <div>
        <h2 class="i-text-subheading-1 i-mb-xs">{{ componentHeading }}</h2>
        <h3 class="i-text-heading-2 i-mb-0">{{ totalAmount }}</h3>
      </div>
      <div v-if="hasData" class="d-flex align-items-center">
        <i-select
          data-testid="chart-view-type"
          :options="dropdownOptions"
          :model-value="selectedView"
          placeholder="Please select a view"
          class="chart-view-type"
          @option-selected="
            (option) => {
              selectedView = option;
            }
          "
        ></i-select>
        <i-menu :close-on-item-click="true" trigger-align="right">
          <template #trigger>
            <i-button
              class="i-ml-4"
              left-icon="small-setting-menu-horizontal"
              :size="iButtonSizes.SMALL"
              :type="iButtonTypes.NEUTRAL"
              :button-style="iButtonStyles.OUTLINE"
            />
          </template>

          <template #items>
            <i-menu-item
              v-for="item in menuItems"
              :key="item.key"
              :label="item.label"
              v-read-only-check="{
                alignment: 'right_middle',
                active: item.readOnlyCheck,
              }"
              @item-click="selectMenuItem(item)"
            />
          </template>
        </i-menu>
      </div>
    </div>
    <div
      class="total-barchart__chart i-radius-xl i-mt-13"
      :class="{ 'total-barchart__chart--empty-state': !hasData }"
    >
      <div
        v-if="!hasData && isLoading"
        class="total-barchart__loading d-flex justify-content-center align-items-stretch"
      >
        <i-spinner size="xl" />
      </div>
      <div v-else-if="hasData && !isLoading">
        <total-barchart-canvas
          :chart-view="selectedView"
          :chart-data="loadedData"
          :chart-tooltip-title="tooltipTitle"
        />
      </div>
      <!-- empty state, add condition -->
      <empty-state
        v-else
        class="i-px-14 i-py-15"
        title="No data found for this period"
        description="There’s not enough data available in the selected range to display the
          chart yet. <br />Please check back again later."
      />
    </div>
  </div>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex';
import {
  iSelect,
  iButton,
  iButtonSizes,
  iButtonStyles,
  iButtonTypes,
  iMenu,
  iMenuItem,
  iSpinner,
} from '@inscribe/signal';
import { toCurrency } from '@/utils/toCurrency';
import TotalBarchartCanvas from '@/V2/credit/charts/total-barchart/TotalBarchartCanvas';
import EmptyState from '@/V2/credit/charts/EmptyState';
import { CHART_MENU_ITEMS } from '@/V2/credit/charts/charts.constants';
import componentText from '@/V2/credit/granular-insights/components/components.text';
import {
  getTransactionAmountProperty,
  getTransactionTotalsProperty,
} from '@/V2/credit/granular-insights/components/components.utils';
import { getAndSyncQueryParams } from '@/utils/queryParams';
import { getUseAllDocsQueryParameter } from '@/V2/credit/utils';

export default {
  components: {
    iSelect,
    iButton,
    iMenu,
    iMenuItem,
    iSpinner,
    EmptyState,
    TotalBarchartCanvas,
  },
  props: {
    type: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      hasData: null,
      isLoading: null,
      selectedView: 'monthly',
      dropdownOptions: [
        {
          value: 'monthly',
          text: 'Monthly view',
        },
        {
          value: 'quarterly',
          text: 'Quarterly view',
        },
      ],
      menuItems: [...CHART_MENU_ITEMS],
      loadedData: null,
      totalAmount: null,
      params: getAndSyncQueryParams({
        fullPath: this.$route.fullPath,
        router: this.$router,
        store: this.$store,
      }),
      iButtonSizes,
      iButtonStyles,
      iButtonTypes,
    };
  },

  computed: {
    ...mapState({
      credit: 'credit',
    }),

    ...mapGetters({
      transactionData: 'insightsTotalTransactionsByTimePeriod',
      insightsTotals: 'insightsTotals',
    }),

    componentHeading() {
      return componentText[this.type].totalBarchart.title;
    },

    tooltipTitle() {
      return componentText[this.type].totalBarchart.tooltipTitle;
    },
    useAllDocsQueryParameter() {
      return getUseAllDocsQueryParameter(this.params);
    },
  },
  watch: {
    '$route.fullPath': {
      handler: function (newFullPath) {
        const oldUseAllDocsValue = this.params?.use_all_docs;
        this.params = getAndSyncQueryParams({
          fullPath: newFullPath,
          router: this.$router,
          store: this.$store,
        });
        if (oldUseAllDocsValue !== this.params?.use_all_docs) {
          this.updateAllData();
        }
      },
    },
  },
  mounted() {
    this.updateAllData();

    this.emitter.on('credit:refresh', async () => {
      this.updateAllData();
    });
  },
  methods: {
    ...mapActions(['getCustomerTransactionsByTimePeriod']),

    updateAllData() {
      this.fetchTotalBarchartData();
    },

    async fetchTotalBarchartData() {
      this.setLoading();

      const queryParams = {
        category: this.type,
        group_by: 'month', // we group by month by default, and generate quarterly data from this
        include_unreconciled_documents: this.useAllDocsQueryParameter,
      };

      try {
        await this.getCustomerTransactionsByTimePeriod({
          customerId: this.$route.params.customer_id,
          queryParams,
        });

        if (this.transactionData.length === 0) {
          this.totalAmount = '$-';
          this.setNoData();
        } else {
          this.loadedData = this.dataFormatMapper(this.transactionData);

          this.totalAmount = toCurrency(
            this.insightsTotals[getTransactionTotalsProperty(this.type)],
          );
          this.setDataLoaded();
        }
      } catch (error) {
        this.setNoData();
      }
    },

    dataFormatMapper(transactions) {
      return transactions
        .filter((transaction) => {
          // Filter out transactions where the value is zero, which would be displayed as a bar with no height
          return transaction[getTransactionAmountProperty(this.type)] !== 0;
        })
        .map((transaction) => ({
          x: transaction.start_date,
          y: transaction[getTransactionAmountProperty(this.type)],
        }));
    },

    selectMenuItem(item) {
      this.emitter.emit('total-barchart-menu-event', item.key);
    },

    setDataLoaded() {
      this.hasData = true;
      this.isLoading = false;
    },

    setLoading() {
      this.hasData = false;
      this.isLoading = true;
    },

    setNoData() {
      this.hasData = false;
      this.isLoading = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.total-barchart {
  &__header {
    .chart-view-type {
      min-width: 155px;
    }
  }

  &__chart {
    background: $light-neutral-default;
    height: 320px;
    border: 1px solid transparent;

    &--empty-state {
      border: 1px solid $light-neutral-60;
    }
  }

  &__loading {
    height: 320px;
  }
}
</style>
